import React, { useEffect, useRef, useState } from 'react'
import { Document, Page } from 'react-pdf'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import IconButton from '@material-ui/core/IconButton'
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore'
import NavigateNextIcon from '@material-ui/icons/NavigateNext'
import RemoveIcon from '@material-ui/icons/Remove'
import AddIcon from '@material-ui/icons/Add'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import type { QuoteFile } from 'paintscout'
import classnames from 'classnames'
import Button from '../Button'
import Spinner from '../Spinner'
import Typography from '../Typography'
import { useTranslation } from '../../providers/TranslationProvider'
import configurePdfWorker from './pdfWorker'

configurePdfWorker()

const ZOOM_STEPS = [50, 70, 90, 100, 120, 140, 160, 180, 200]
const DEFAULT_ZOOM_INDEX = 3

export type PdfPreviewLayout = 'desktop' | 'mobile'

export interface FileViewerPdfPreviewProps {
  file: QuoteFile
  layout?: PdfPreviewLayout
  showOpenButton?: boolean
}

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      gap: theme.spacing(1)
    },
    desktopFrame: {
      border: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      borderRadius: theme.borderRadius.md,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      background: theme.palette.common.white
    },
    documentWrapper: {
      position: 'relative',
      width: '100%',
      flex: '1 1 auto',
      minHeight: 320,
      maxHeight: '60vh',
      overflow: 'auto',
      backgroundColor: theme.paintscout.palette.brand.Neutral.P96,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start'
    },
    documentWrapperDesktop: {
      maxHeight: 480
    },
    documentWrapperMobile: {
      maxHeight: '55vh',
      borderRadius: 0
    },
    barRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      padding: theme.spacing(0.5, 0),
      background: theme.palette.common.white
    },
    barRowInside: {
      padding: theme.spacing(1, 1.25),
      borderTop: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
    },
    barGroup: {
      display: 'inline-flex',
      alignItems: 'stretch',
      borderRadius: theme.borderRadius.md,
      border: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
      background: theme.palette.common.white,
      overflow: 'hidden',
      '& > * + *': {
        borderLeft: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`
      }
    },
    barBtn: {
      width: 40,
      height: 36,
      borderRadius: 0,
      color: theme.paintscout.palette.brand.Neutral.P30,
      '&:hover': {
        background: theme.paintscout.palette.brand.Neutral.P96
      }
    },
    barLabel: {
      ...theme.typography.body2,
      color: theme.paintscout.palette.brand.Neutral.P30,
      minWidth: 56,
      height: 36,
      padding: theme.spacing(0, 1.5),
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      userSelect: 'none'
    },
    openButton: {
      display: 'flex',
      justifyContent: 'flex-end'
    },
    errorMessage: {
      padding: theme.spacing(2)
    }
  }),
  { name: 'FileViewerPdfPreview' }
)

export default function FileViewerPdfPreview({
  file,
  layout = 'desktop',
  showOpenButton = false
}: FileViewerPdfPreviewProps) {
  const classes = useStyles({})
  const { __ } = useTranslation()
  const [numPages, setNumPages] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [zoomIndex, setZoomIndex] = useState(DEFAULT_ZOOM_INDEX)
  const [containerWidth, setContainerWidth] = useState<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const node = containerRef.current
    if (!node) return
    setContainerWidth(node.clientWidth)
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  // Reset paging when switching to a different PDF so we always open on page 1
  // and don't request a page index that doesn't exist in the new document (#21098)
  useEffect(() => {
    setCurrentPage(1)
    setNumPages(0)
  }, [file.src])

  const zoom = ZOOM_STEPS[zoomIndex]
  const pageWidth = containerWidth ? containerWidth * (zoom / 100) : undefined
  const canZoomOut = zoomIndex > 0
  const canZoomIn = zoomIndex < ZOOM_STEPS.length - 1
  const canPrev = currentPage > 1
  const canNext = currentPage < numPages
  const showPaging = numPages > 1

  const handleOpen = () => {
    if (file.src && file.src.startsWith('https://')) {
      window.open(file.src, '_blank', 'noopener,noreferrer')
    }
  }

  const bar = (
    <div className={classnames(classes.barRow, layout === 'desktop' && classes.barRowInside)}>
      <div className={classes.barGroup}>
        <IconButton
          className={classes.barBtn}
          disabled={!canZoomOut}
          onClick={() => setZoomIndex((i) => Math.max(0, i - 1))}
          aria-label={__('Zoom out')}
        >
          <RemoveIcon fontSize="small" />
        </IconButton>
        <span className={classes.barLabel}>{zoom} %</span>
        <IconButton
          className={classes.barBtn}
          disabled={!canZoomIn}
          onClick={() => setZoomIndex((i) => Math.min(ZOOM_STEPS.length - 1, i + 1))}
          aria-label={__('Zoom in')}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </div>

      {showPaging && (
        <div className={classes.barGroup}>
          <IconButton
            className={classes.barBtn}
            disabled={!canPrev}
            onClick={() => setCurrentPage((p) => p - 1)}
            aria-label={__('Previous page')}
          >
            <NavigateBeforeIcon fontSize="small" />
          </IconButton>
          <span className={classes.barLabel}>
            {currentPage} / {numPages}
          </span>
          <IconButton
            className={classes.barBtn}
            disabled={!canNext}
            onClick={() => setCurrentPage((p) => p + 1)}
            aria-label={__('Next page')}
          >
            <NavigateNextIcon fontSize="small" />
          </IconButton>
        </div>
      )}
    </div>
  )

  const documentBlock = (
    <div
      ref={containerRef}
      className={classnames(
        classes.documentWrapper,
        layout === 'desktop' ? classes.documentWrapperDesktop : classes.documentWrapperMobile
      )}
    >
      <Document
        file={file.src}
        onLoadSuccess={({ numPages: n }) => setNumPages(n)}
        loading={<Spinner />}
        error={
          <Typography variant="body2" color="error" align="center" className={classes.errorMessage}>
            {__('Failed to load PDF.')}{' '}
            {file.src?.startsWith('https://') && (
              <a href={file.src} target="_blank" rel="noopener noreferrer">
                {__('Open in browser')}
              </a>
            )}
          </Typography>
        }
      >
        <Page pageNumber={currentPage} width={pageWidth} renderTextLayer={false} renderAnnotationLayer={false} />
      </Document>
    </div>
  )

  return (
    <div className={classes.root}>
      {layout === 'desktop' ? (
        <div className={classes.desktopFrame}>
          {documentBlock}
          {bar}
        </div>
      ) : (
        <>
          {documentBlock}
          {bar}
        </>
      )}

      {showOpenButton && file.src?.startsWith('https://') && (
        <div className={classes.openButton}>
          <Button variant="text" startIcon={<OpenInNewIcon fontSize="small" />} onClick={handleOpen}>
            {__('Open PDF')}
          </Button>
        </div>
      )}
    </div>
  )
}
